import * as React from "react";
import {useContext, useEffect, useState} from "react";

import {Button} from "primereact/button";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";

import CustomView from "../../component/CustomView";
import Combo from "../../component/form/combo/Combo";
import MultiCombo from "../../component/form/multi-combo/MultiCombo";
import {DestinoNotificacao, RotuloDestinoNotificacao} from "../../model/DestinoNotificacao";
import Restaurante from "../../model/Restaurante";
import {NotificacaoEnvioResultado} from "../../dto/NotificacaoEnvioResultado";
import {MensagemContext} from "../../context/MensagemContext";
import {MensagemContextType} from "../../context/MensagemContextType";
import {Ajax} from "../../utils/Ajax";
import rest from "../../utils/EnvConfig";
import {useKeycloak} from "../../keycloak/useKeycloak";
import {
    descreverDestino,
    extrairMensagemErro,
    mensagemResultado,
    montarNotificacaoEnvio,
    NotificacaoEnvioForm,
    TEXTO_MAX_CARACTERES,
    TITULO_MAX_CARACTERES,
    validarNotificacaoEnvio
} from "../../utils/NotificacaoEnvioUtils";

const opcoesDestino = Object.values(DestinoNotificacao)
    .map(destino => ({value: destino, label: RotuloDestinoNotificacao[destino]}));

function NotificacaoEnvioView() {
    const {token} = useKeycloak();
    const {info, warning, error} = useContext(MensagemContext) as MensagemContextType;
    const [destino, setDestino] = useState<DestinoNotificacao>();
    const [restauranteUuids, setRestauranteUuids] = useState<string[]>([]);
    const [titulo, setTitulo] = useState<string>("");
    const [texto, setTexto] = useState<string>("");
    const [restaurantes, setRestaurantes] = useState<Restaurante[]>([]);
    const [enviando, setEnviando] = useState<boolean>(false);

    const form = (): NotificacaoEnvioForm => ({destino, restauranteUuids, titulo, texto});

    useEffect(() => {
        if (destino !== DestinoNotificacao.CLIENTE || restaurantes.length > 0) return;

        Ajax.of()
            .error(error)
            .setTokenKeycloak(token!)
            .obterTodosCustom(`${rest.restaurante}/${rest.obterTodos}`, (data) => {
                setRestaurantes(data);
            });
    }, [destino]);

    const alterarDestino = (novoDestino: DestinoNotificacao) => {
        setDestino(novoDestino);
        if (novoDestino !== DestinoNotificacao.CLIENTE) setRestauranteUuids([]);
    }

    const limparFormulario = () => {
        setDestino(undefined);
        setRestauranteUuids([]);
        setTitulo("");
        setTexto("");
    }

    const enviar = async () => {
        setEnviando(true);

        await Ajax.of()
            .setTokenKeycloak(token!)
            .postRegistroCustom(`${rest.server}${rest.notificacao}/enviar`, montarNotificacaoEnvio(form()),
                (resultado: NotificacaoEnvioResultado) => {
                    const mensagem = mensagemResultado(resultado);
                    if (mensagem.tipo === "info") {
                        info(mensagem.texto);
                        limparFormulario();
                    } else {
                        warning(mensagem.texto);
                    }
                    setEnviando(false);
                },
                (erro: Error) => {
                    error(extrairMensagemErro(erro));
                    setEnviando(false);
                });
    }

    const confirmarEnvio = () => {
        const mensagemValidacao = validarNotificacaoEnvio(form());
        if (mensagemValidacao) {
            warning(mensagemValidacao);
            return;
        }

        confirmDialog({
            message: `Enviar esta notificação para ${descreverDestino(form())}?`,
            header: 'Atenção',
            icon: 'pi pi-info-circle',
            defaultFocus: 'reject',
            acceptLabel: "Sim",
            rejectLabel: "Não",
            accept: enviar
        });
    }

    return (
        <CustomView id={"notificacao"} title={"Enviar notificação"}
                    mostrarCancelar={false} mostrarBarraBotoes={false}>
            <Combo id={"destino"} label={"Destino"}
                   value={destino}
                   values={opcoesDestino}
                   optionLabel={"label"}
                   optionValue={"value"}
                   placeHolder={"Selecione o destino"}
                   onChangeHandler={(e: any) => alterarDestino(e.value)}/>

            {destino === DestinoNotificacao.CLIENTE && (
                <>
                    <MultiCombo id={"restaurantes"} label={"Restaurantes"}
                                values={restaurantes}
                                value={restauranteUuids}
                                optionLabel={"nomeEstabelecimento"}
                                optionValue={"uuid"}
                                filter={true}
                                onChangeHandler={(e: any) => setRestauranteUuids(e.value)}/>
                    <small>Sem restaurante escolhido, envia para todos os clientes.</small>
                </>
            )}

            <div className="p-fluid grid">
                <div className="field col-12 md:col-4">
                    <label htmlFor="titulo">Título</label>
                    <InputText id="titulo" value={titulo} maxLength={TITULO_MAX_CARACTERES}
                               onChange={(e) => setTitulo(e.target.value)}/>
                </div>
            </div>

            <div className="p-fluid grid">
                <div className="field col-12 md:col-4">
                    <label htmlFor="texto">Texto</label>
                    <InputTextarea id="texto" value={texto} maxLength={TEXTO_MAX_CARACTERES} rows={5}
                                   onChange={(e) => setTexto(e.target.value)}/>
                </div>
            </div>

            <Button label={"Enviar"} icon={"pi pi-send"} raised size={"large"}
                    disabled={enviando} loading={enviando}
                    onClick={confirmarEnvio}/>

            <ConfirmDialog/>
        </CustomView>
    );
}

export default NotificacaoEnvioView;
